import { useSyncExternalStore } from 'react'

/**
 * Whether Eco's panel is open, without a provider: the rail's button, the
 * field app's button and the keyboard shortcut all reach the one panel
 * mounted by the shell. A prompt handed to `openEco` is sent as soon as the
 * panel has a conversation ("Ask Eco about this job"). Decision 116.
 */

interface EcoState {
  open: boolean
  /** Sent once on opening, then cleared. */
  prompt: string | null
  /** The conversation on screen, kept when the panel becomes the page and back. */
  sessionId: string | null
}

let state: EcoState = { open: false, prompt: null, sessionId: null }
const listeners = new Set<() => void>()

function set(next: EcoState) {
  state = next
  for (const listener of listeners) listener()
}

export function openEco(prompt: string | null = null): void {
  set({ ...state, open: true, prompt })
}

export function closeEco(): void {
  set({ ...state, open: false, prompt: null })
}

export function toggleEco(): void {
  set({ ...state, open: !state.open, prompt: null })
}

export function setEcoSession(sessionId: string | null): void {
  if (state.sessionId !== sessionId) set({ ...state, sessionId })
}

export function takeEcoPrompt(): string | null {
  const prompt = state.prompt
  if (prompt !== null) state = { ...state, prompt: null }
  return prompt
}

export function useEcoState(): EcoState {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener)
      return () => listeners.delete(listener)
    },
    () => state,
    () => state,
  )
}
